import React, { useState, useCallback, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Paper,
  Typography,
  Grid,
  Container,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Button,
  Fab,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  MarkerType,
  Position,
  applyNodeChanges,
  applyEdgeChanges,
  useEdgesState,
  Handle,
  getBezierPath,
} from "reactflow";
import "reactflow/dist/style.css";
import "./App.css";
import "./index.css";
import styled from "@emotion/styled";
import axios from "axios";
import { toPng } from "html-to-image"; // Import html-to-ima
import { SignIn, SignUp, UserButton, useUser } from "@clerk/clerk-react";
import { useAuth } from "@clerk/clerk-react";
import ClarityAnalytics from "./components/clarity";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Add as AddIcon,
  Folder as FolderIcon,
  Menu as MenuIcon,
  HelpOutline as HelpOutlineIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { addProjects, getAllProjects } from "../src/services/projectsService";
import {
  filterUnified_table,
  getAllUnified_table,
  addUnified_table,
  updateUnified_table,
} from "../src/services/unified_tableService";
import { codeGeneratorService } from "../src/services/codeGenerateService";
import { getCodeGeneratorStatusService } from "../src/services/getCodeGeneratorStatus";
import { getAllCredits } from "../src/services/creditsService";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import {
  getProjectLinksService,
  getProjectDownloadUrlsService,
} from "../src/services/codeGenerateService";
import ProjectDescriptionTab from "./components/tabs/ProjectDescriptionTab";
import RequirementsTab from "./components/tabs/RequirementsTab";
import UserStoriesTab from "./components/tabs/UserStoriesTab";
import FlowContainer from "./components/FlowContainer";
import SitemapTab from "./components/tabs/SitemapTab";
import DataModelsTab from "./components/tabs/DataModelsTab";
import BackendApiTab from "./components/tabs/BackendApiTab";
import RequirementNode from "./components/nodes/RequirementNode";
import UserStoryNode from "./components/nodes/UserStoryNode";
import SitemapNode from "./components/nodes/SitemapNode";
import DataModelNode from "./components/nodes/DataModelNode";
import prompts from "./constants/prompts";
import AuthWrapper from "./components/AuthWrapper";
import NavigationHeader from "./components/NavigationHeader";
import ProjectDrawer from "./components/ProjectDrawer";
import { PROJECT_TEMPLATES } from "./projectTemplates";

// Sample Data
const sampleData = {
  project: [],
  requirements: [],
  userStories: [],
  sitemap: [],
  dataModels: [],
};

const initialNodes = {
  requirements: [
    {
      id: "req-1",
      type: "requirement",
      data: {
        label: "User Authentication",
        description:
          "System must provide secure user authentication with email and password",
      },
    },
    {
      id: "req-2",
      type: "requirement",
      data: {
        label: "Product Management",
        description:
          "Admin users should be able to manage product inventory and categories",
      },
    },
    {
      id: "req-3",
      type: "requirement",
      data: {
        label: "Shopping Cart",
        description:
          "Users must be able to add/remove items from cart and proceed to checkout",
      },
    },
    {
      id: "req-4",
      type: "requirement",
      data: {
        label: "Order Processing",
        description:
          "System should handle order processing and payment integration",
      },
    },
    {
      id: "req-5",
      type: "requirement",
      data: {
        label: "User Reviews",
        description: "Customers can leave reviews and ratings for products",
      },
    },
  ],
  userStories: [
    {
      id: "us-1",
      type: "userStory",
      data: {
        role: "customer",
        want: "create an account",
        benefit: "save my shopping preferences and order history",
      },
    },
    {
      id: "us-2",
      type: "userStory",
      data: {
        role: "admin",
        want: "manage product inventory",
        benefit: "keep track of stock levels and update product information",
      },
    },
    {
      id: "us-3",
      type: "userStory",
      data: {
        role: "customer",
        want: "filter products by category",
        benefit: "find items I'm interested in quickly",
      },
    },
    {
      id: "us-4",
      type: "userStory",
      data: {
        role: "customer",
        want: "add items to cart",
        benefit: "purchase multiple items at once",
      },
    },
  ],
  sitemap: [
    {
      id: "st-1",
      type: "sitemap",
      position: { x: 400, y: 50 },
      data: {
        label: "Home",
        type: "main",
        description: "Main landing page with featured products",
      },
    },
    {
      id: "st-2",
      type: "sitemap",
      position: { x: 200, y: 200 },
      data: {
        label: "Products",
        type: "section",
        description: "Product catalog and categories",
      },
    },
    {
      id: "st-3",
      type: "sitemap",
      position: { x: 600, y: 200 },
      data: {
        label: "Account",
        type: "section",
        description: "User account management",
      },
    },
    {
      id: "st-4",
      type: "sitemap",
      position: { x: 100, y: 350 },
      data: {
        label: "Category View",
        type: "screen",
        description: "Product listing by category",
      },
    },
    {
      id: "st-5",
      type: "sitemap",
      position: { x: 300, y: 350 },
      data: {
        label: "Product Details",
        type: "screen",
        description: "Individual product page",
      },
    },
  ],
  dataModels: [
    {
      id: "dm-1",
      type: "dataModel",
      position: { x: 100, y: 100 },
      data: {
        name: "User",
        attributes: [
          { name: "id", type: "UUID" },
          { name: "email", type: "String" },
          { name: "password", type: "String" },
          { name: "name", type: "String" },
          { name: "role", type: "Enum" },
          { name: "createdAt", type: "DateTime" },
        ],
      },
    },
    {
      id: "dm-2",
      type: "dataModel",
      position: { x: 400, y: 100 },
      data: {
        name: "Product",
        attributes: [
          { name: "id", type: "UUID" },
          { name: "name", type: "String" },
          { name: "description", type: "Text" },
          { name: "price", type: "Decimal" },
          { name: "categoryId", type: "UUID" },
          { name: "stock", type: "Integer" },
          { name: "createdAt", type: "DateTime" },
        ],
      },
    },
    {
      id: "dm-3",
      type: "dataModel",
      position: { x: 700, y: 100 },
      data: {
        name: "Order",
        attributes: [
          { name: "id", type: "UUID" },
          { name: "userId", type: "UUID" },
          { name: "status", type: "String" },
          { name: "total", type: "Decimal" },
          { name: "createdAt", type: "DateTime" },
        ],
      },
    },
    {
      id: "dm-4",
      type: "dataModel",
      position: { x: 100, y: 300 },
      data: {
        name: "Category",
        attributes: [
          { name: "id", type: "UUID" },
          { name: "name", type: "String" },
          { name: "description", type: "Text" },
          { name: "parentId", type: "UUID" },
          { name: "createdAt", type: "DateTime" },
        ],
      },
    },
    {
      id: "dm-5",
      type: "dataModel",
      position: { x: 400, y: 300 },
      data: {
        name: "Review",
        attributes: [
          { name: "id", type: "UUID" },
          { name: "userId", type: "UUID" },
          { name: "productId", type: "UUID" },
          { name: "rating", type: "Integer" },
          { name: "comment", type: "Text" },
          { name: "createdAt", type: "DateTime" },
        ],
      },
    },
    {
      id: "dm-6",
      type: "dataModel",
      position: { x: 700, y: 300 },
      data: {
        name: "OrderItem",
        attributes: [
          { name: "id", type: "UUID" },
          { name: "orderId", type: "UUID" },
          { name: "productId", type: "UUID" },
          { name: "quantity", type: "Integer" },
          { name: "price", type: "Decimal" },
        ],
      },
    },
  ],
};

const initialEdges = {
  requirements: [],
  userStories: [],
  sitemap: [],
  dataModels: [],
};

const EditDialog = ({ open, onClose, onSave, initialValue, title }) => {
  const [editedText, setEditedText] = useState(initialValue);

  // Reset edited text when dialog opens with new initial value
  useEffect(() => {
    setEditedText(initialValue);
  }, [initialValue]);

  const handleClose = () => {
    setEditedText(initialValue); // Reset text
    onClose();
  };

  const handleSave = () => {
    onSave(editedText);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={editedText}
          onChange={(e) => setEditedText(e.target.value)}
          margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const customNodes = {
  requirement: RequirementNode,
  userStory: UserStoryNode,
  sitemap: SitemapNode,
  dataModel: DataModelNode,
};

// Custom edge component
const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
  label,
}) => {
  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <path
        id={id}
        style={{
          ...style,
          stroke: "#64748b",
          strokeWidth: 2,
        }}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />
      {label && (
        <text>
          <textPath
            href={`#${id}`}
            style={{
              fill: "#64748b",
              fontWeight: 500,
              fontSize: "12px",
            }}
            startOffset="50%"
            textAnchor="middle"
          >
            {label}
          </textPath>
        </text>
      )}
    </>
  );
};

const edgeTypes = {
  custom: CustomEdge,
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      {...other}
      className="h-full"
      style={{ height: "calc(100vh - 120px)", marginTop: "100px" }}
    >
      {value === index && (
        <Container maxWidth="xl" className="h-full py-6">
          {children}
        </Container>
      )}
    </div>
  );
}

// Add these constants at the top of the file with other constants
const NODE_WIDTH = 500; // Width of the largest node type
const NODE_HEIGHT = 500; // Height of the largest node type
const NODE_MARGIN = 500; // Minimum margin between nodes
const GRID_COLUMNS = 5; // Number of columns in the grid layout

// Add this helper function before the handleMessage function
const calculateNodePosition = (index, existingNodes = []) => {
  // Create a wider spread for nodes
  const SPREAD_FACTOR = 500; // Increased from previous margin

  // Calculate position with more randomness
  let position = {
    x: (Math.random() - 0.5) * SPREAD_FACTOR * 4, // Multiply by 4 for wider horizontal spread
    y: (Math.random() - 0.5) * SPREAD_FACTOR * 3, // Multiply by 3 for wider vertical spread
  };

  // Ensure minimum distance between nodes
  const MIN_DISTANCE = 500;
  let attempts = 0;
  const MAX_ATTEMPTS = 100;

  while (
    attempts < MAX_ATTEMPTS &&
    existingNodes?.some((node) => {
      const dx = Math.abs(node.position.x - position.x);
      const dy = Math.abs(node.position.y - position.y);
      return Math.sqrt(dx * dx + dy * dy) < MIN_DISTANCE;
    })
  ) {
    position = {
      x: (Math.random() - 0.5) * SPREAD_FACTOR * 4,
      y: (Math.random() - 0.5) * SPREAD_FACTOR * 3,
    };
    attempts++;
  }

  return position;
};

// Add this debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

function App() {
  const [showDemo, setShowDemo] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentEdit, setCurrentEdit] = useState(null);

  // Initialize states with sample data
  const [projectMessages, setProjectMessages] = useState([]);
  const [requirementMessages, setRequirementMessages] = useState([]);
  const [userStoryMessages, setUserStoryMessages] = useState([]);
  const [sitemapMessages, setSitemapMessages] = useState([]);
  const [dataModelMessages, setDataModelMessages] = useState([]);

  // Project-specific markdown responses
  const [projectMarkdown, setProjectMarkdown] = useState([]);

  // Initialize nodes and edges
  const [requirementNodes, setRequirementNodes] = useState([]);
  const [userStoryNodes, setUserStoryNodes] = useState([]);
  const [sitemapNodes, setSitemapNodes] = useState([]);
  const [dataModelNodes, setDataModelNodes] = useState([]);

  const [requirementEdges, setRequirementEdges] = useState([]);
  const [userStoryEdges, setUserStoryEdges] = useState([]);
  const [sitemapEdges, setSitemapEdges] = useEdgesState([]);
  const [dataModelEdges, setDataModelEdges] = useState([]);

  // Add this new state for the error dialog
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Add new state for SQL
  const [generatedSQL, setGeneratedSQL] = useState("");

  // Add new state variables after other state declarations
  const [requirementSummary, setRequirementSummary] = useState([]);
  const [userStorySummary, setUserStorySummary] = useState([]);
  const [sitemapSummary, setSitemapSummary] = useState([]);
  const [dataModelSummary, setDataModelSummary] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectName, setSelectedProjectName] = useState(null);
  const serializeData = (state) => {
    try {
      // Store complete arrays without any transformations
      const serialized = {
        project_messages: JSON.stringify(state.projectMessages),
        requirement_messages: JSON.stringify(state.requirementMessages),
        user_story_messages: JSON.stringify(state.userStoryMessages),
        sitemap_messages: JSON.stringify(state.sitemapMessages),
        data_model_messages: JSON.stringify(state.dataModelMessages),
        projectMarkdown: JSON.stringify(state.projectMarkdown),
        requirement_nodes: JSON.stringify(state.requirementNodes),
        user_story_nodes: JSON.stringify(state.userStoryNodes),
        sitemap_nodes: JSON.stringify(state.sitemapNodes),
        data_model_nodes: JSON.stringify(state.dataModelNodes),
        requirement_edges: JSON.stringify(state.requirementEdges),
        user_story_edges: JSON.stringify(state.userStoryEdges),
        sitemap_edges: JSON.stringify(state.sitemapEdges),
        data_model_edges: JSON.stringify(state.dataModelEdges),
        generated_sql: state.generatedSQL,
        requirement_summary: JSON.stringify(state.requirementSummary),
        user_story_summary: JSON.stringify(state.userStorySummary),
        sitemap_summary: JSON.stringify(state.sitemapSummary),
        data_model_summary: JSON.stringify(state.dataModelSummary),
      };

      // Validate that all data was properly serialized
      Object.entries(serialized).forEach(([key, value]) => {
        if (value === undefined || value === "undefined" || value === null) {
          console.warn(`Warning: ${key} was not properly serialized`);
          serialized[key] = JSON.stringify([]); // Fallback to empty array
        }
      });

      return serialized;
    } catch (error) {
      console.error("Error in serializeData:", error);
      // Log the problematic state
      console.error("Problematic state:", {
        requirementNodes: state.requirementNodes,
        requirementEdges: state.requirementEdges,
        // ... other relevant state
      });
      throw error;
    }
  };
  const deserializeData = (data) => {
    return {
      projectMessages: JSON.parse(data.project_messages || "[]"),
      requirementMessages: JSON.parse(data.requirement_messages || "[]"),
      userStoryMessages: JSON.parse(data.user_story_messages || "[]"),
      sitemapMessages: JSON.parse(data.sitemap_messages || "[]"),
      dataModelMessages: JSON.parse(data.data_model_messages || "[]"),
      projectMarkdown: JSON.parse(data.projectMarkdown || "[]"),
      requirementNodes: JSON.parse(data.requirement_nodes || "[]"),
      userStoryNodes: JSON.parse(data.user_story_nodes || "[]"),
      sitemapNodes: JSON.parse(data.sitemap_nodes || "[]"),
      dataModelNodes: JSON.parse(data.data_model_nodes || "[]"),
      requirementEdges: JSON.parse(data.requirement_edges || "[]"),
      userStoryEdges: JSON.parse(data.user_story_edges || "[]"),
      sitemapEdges: JSON.parse(data.sitemap_edges || "[]"),
      dataModelEdges: JSON.parse(data.data_model_edges || "[]"),
      generatedSQL: data.generated_sql || "",
      requirementSummary: JSON.parse(data.requirement_summary || "[]"),
      userStorySummary: JSON.parse(data.user_story_summary || "[]"),
      sitemapSummary: JSON.parse(data.sitemap_summary || "[]"),
      dataModelSummary: JSON.parse(data.data_model_summary || "[]"),
    };
  };

  const setEveryStateToEmpty = () => {
    setProjectMessages([]);
    setRequirementMessages([]);
    setUserStoryMessages([]);
    setSitemapMessages([]);
    setDataModelMessages([]);
    setProjectMarkdown([]);
    setRequirementNodes([]);
    setUserStoryNodes([]);
    setSitemapNodes([]);
    setDataModelNodes([]);
    setRequirementEdges([]);
    setUserStoryEdges([]);
    setSitemapEdges([]);
    setDataModelEdges([]);
    setGeneratedSQL("");
    setRequirementSummary([]);
    setUserStorySummary([]);
    setSitemapSummary([]);
    setDataModelSummary([]);
  };

  const handleProjectSelect = async (projectId) => {
    const freshToken = await getToken();
    localStorage.setItem("clerk-token", freshToken);
    setSelectedProject(() => projectId);

    // Find and set the project name
    const project = projects.find((p) => p.project_id === projectId);
    setSelectedProjectName(project?.project_name || null);

    setIsLoading(true);

    try {
      // Format the payload according to the API requirements
      const filterPayload = [
        {
          columnName: "project_id", // The column to filter on
          columnCondition: 1, // Assuming 1 means "equals"
          columnValue: projectId.toString(), // Convert to string if needed
        },
      ];

      const response = await filterUnified_table(filterPayload);
      if (response?.data?.code === 1 && response?.data?.document?.records) {
        const unifiedData = deserializeData(response.data.document.records[0]);

        // Update all states with fetched data
        setProjectMessages(unifiedData.projectMessages);
        setRequirementMessages(unifiedData.requirementMessages);
        setUserStoryMessages(unifiedData.userStoryMessages);
        setSitemapMessages(unifiedData.sitemapMessages);
        setDataModelMessages(unifiedData.dataModelMessages);
        setProjectMarkdown(unifiedData.projectMarkdown);
        setRequirementNodes(unifiedData.requirementNodes);
        setUserStoryNodes(unifiedData.userStoryNodes);
        setSitemapNodes(unifiedData.sitemapNodes);
        setDataModelNodes(unifiedData.dataModelNodes);
        setRequirementEdges(unifiedData.requirementEdges);
        setUserStoryEdges(unifiedData.userStoryEdges);
        setSitemapEdges(unifiedData.sitemapEdges);
        setDataModelEdges(unifiedData.dataModelEdges);
        setGeneratedSQL(unifiedData.generatedSQL);
        setRequirementSummary(unifiedData.requirementSummary);
        setUserStorySummary(unifiedData.userStorySummary);
        setSitemapSummary(unifiedData.sitemapSummary);
        setDataModelSummary(unifiedData.dataModelSummary);
      } else {
        setEveryStateToEmpty();
      }
      setCurrentTab(0);
    } catch (error) {
      console.error("Error fetching unified data:", error);
      setErrorMessage("Failed to load project data");
      setErrorDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Create a debounced version of saveUnifiedData
  const debouncedSave = useCallback(
    debounce(async (completion) => {
      if (!selectedProject) return;

      const currentState = {
        projectMessages,
        requirementMessages,
        userStoryMessages,
        sitemapMessages,
        dataModelMessages,
        projectMarkdown,
        requirementNodes,
        userStoryNodes,
        sitemapNodes,
        dataModelNodes,
        requirementEdges,
        userStoryEdges,
        sitemapEdges,
        dataModelEdges,
        generatedSQL,
        requirementSummary,
        userStorySummary,
        sitemapSummary,
        dataModelSummary,
      };

      try {
        await saveUnifiedData(completion);
      } catch (error) {
        console.error("Error in auto-save:", error);
      }
    }, 10000), // 10 second delay
    [
      selectedProject,
      projectMessages,
      requirementMessages,
      userStoryMessages,
      sitemapMessages,
      dataModelMessages,
      projectMarkdown,
      requirementNodes,
      userStoryNodes,
      sitemapNodes,
      dataModelNodes,
      requirementEdges,
      userStoryEdges,
      sitemapEdges,
      dataModelEdges,
      generatedSQL,
      requirementSummary,
      userStorySummary,
      sitemapSummary,
      dataModelSummary,
    ]
  );

  // Add effect to trigger auto-save when state changes
  useEffect(() => {
    if (selectedProject) {
      debouncedSave();
    }
  }, [
    selectedProject,
    projectMessages,
    requirementMessages,
    userStoryMessages,
    sitemapMessages,
    dataModelMessages,
    projectMarkdown,
    requirementNodes,
    userStoryNodes,
    sitemapNodes,
    dataModelNodes,
    requirementEdges,
    userStoryEdges,
    sitemapEdges,
    dataModelEdges,
    generatedSQL,
    requirementSummary,
    userStorySummary,
    sitemapSummary,
    dataModelSummary,
  ]);

  const saveUnifiedData = async (completion) => {
    console.log("Saving unified data");
    if (!selectedProject) return;

    const currentState = {
      projectMessages,
      requirementMessages,
      userStoryMessages,
      sitemapMessages,
      dataModelMessages,
      projectMarkdown,
      requirementNodes,
      userStoryNodes,
      sitemapNodes,
      dataModelNodes,
      requirementEdges,
      userStoryEdges,
      sitemapEdges,
      dataModelEdges,
      generatedSQL,
      requirementSummary,
      userStorySummary,
      sitemapSummary,
      dataModelSummary,
    };

    // Get current timestamp and user info
    const now = new Date().toISOString();
    const currentUser = "current_user"; // Replace with actual user info from your auth system
    const freshToken = await getToken();
    localStorage.setItem("clerk-token", freshToken);
    try {
      // First, get the existing record to get unified_table_id

      const filterPayload = [
        {
          columnName: "project_id",
          columnCondition: 1, // Assuming 1 means "equals"
          columnValue: selectedProject.toString(),
        },
      ];

      const existingRecord = await filterUnified_table(filterPayload);

      const unified_table_id =
        existingRecord?.data?.document?.records[0]?.unified_table_id;

      if (!unified_table_id) {
        // If no existing record, create new one using addUnified_table
        const serializedData = {
          project_id: selectedProject,
          version_number: 1,
          ...serializeData(currentState),
          completion_tokens: completion?.usage?.completion_tokens || 0,
          prompt_tokens: completion?.usage?.prompt_tokens || 0,
          total_tokens: completion?.usage?.total_tokens || 0,
          isActive: 1,
          createdAt: now,
          createdBy: currentUser,
          modifiedAt: now,
          modifiedBy: currentUser,
        };

        const response = await addUnified_table(serializedData);
        if (response?.data?.code !== 1) {
          throw new Error("Failed to create project data");
        }
      } else {
        // Update existing record
        const serializedData = {
          unified_table_id: unified_table_id,
          project_id: selectedProject,
          version_number: 1, // You might want to increment this
          ...serializeData(currentState),
          completion_tokens: completion?.usage?.completion_tokens || 0,
          prompt_tokens: completion?.usage?.prompt_tokens || 0,
          total_tokens: completion?.usage?.total_tokens || 0,
          isActive: 1,
          modifiedAt: now,
          modifiedBy: currentUser,
        };

        const response = await updateUnified_table(
          unified_table_id,
          serializedData
        );
        if (response?.data?.code !== 1) {
          throw new Error("Failed to update project data");
        }
      }
    } catch (error) {
      console.error("Error saving unified data:", error);
      setErrorMessage("Failed to save project data");
      setErrorDialogOpen(true);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleMessage = async (
    text,
    type,
    setMessages,
    setNodes,
    setEdges,
    setSummary,
    isInitial = false
  ) => {
    const Nodes = {
      project: projectMarkdown,
      requirement: requirementNodes,
      userStory: userStoryNodes,
      sitemap: { sitemapNodes: sitemapNodes, sitemapEdges: sitemapEdges },
      dataModel: {
        dataModelNodes: dataModelNodes,
        dataModelEdges: dataModelEdges,
      },
    };
    setIsLoading(true);
    const freshToken = await getToken();
    localStorage.setItem("clerk-token", freshToken);
    const newMessage = { text, isUser: true };
    await setMessages((prev) => [...prev, newMessage]);
    let responseNodes = [];
    let responseEdges = [];
    const systemPrompt = prompts[type];
    let UserInput = text;
    let finalUserInput = "";
    if (type === "project") {
      let combined;
      if (isInitial || projectMarkdown.length === 0) {
        combined =
          "Should include project overview, plan, key features and Project goals " +
          " Project summary : " +
          text;
      } else {
        combined =
          "Previous description : " +
          projectMarkdown[projectMarkdown.length - 1] +
          "Update the project description by incorporating the following changes while keeping the prior details intact unless specifically instructed to remove or replace them.Please provide whole new response which include all the current information with required changes.The updated details are: " +
          text;
      }

      finalUserInput = combined.trim();
    } else {
      if (!isInitial) {
        if (type == "dataModel") {
          if (dataModelNodes.length > 0) {
            let curNode = dataModelNodes[dataModelNodes.length - 1];
            finalUserInput =
              "Previous response: node" + JSON.stringify(curNode);
          }
          if (dataModelEdges.length > 0) {
            let curEdges = dataModelEdges[dataModelEdges.length - 1];
            finalUserInput =
              finalUserInput + " Edges " + JSON.stringify(curEdges);
          }
        } else if (type == "sitemap") {
          if (sitemapNodes.length > 0) {
            let curNode = sitemapNodes[sitemapNodes.length - 1];
            finalUserInput =
              "Previous response: node" + JSON.stringify(curNode);
          }
          if (sitemapEdges.length > 0) {
            let curEdges = sitemapEdges[sitemapEdges.length - 1];
            finalUserInput =
              finalUserInput + " Edges " + JSON.stringify(curEdges);
          }
        } else if (Nodes[type].length > 0) {
          finalUserInput =
            "Previous response " +
            JSON.stringify(Nodes[type][Nodes[type].length - 1]);
        }
      }

      finalUserInput =
        finalUserInput +
        " User Prompt : " +
        UserInput +
        " Please analyse the following prompt and in response give updated Nodes in the same format.Please provide whole new response which include all the current information with required changes.";
    }

    try {
      // Add these constants at the top of the file
      const MAX_RETRIES = 5;
      const INITIAL_RETRY_DELAY = 1000; // 1 second

      // Add this helper function for delay
      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      // Add retry logic with exponential backoff
      let retryCount = 0;
      let lastResponse;

      while (retryCount < MAX_RETRIES) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/Handle_Gpt_API/chat`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("clerk-token")}`,
              },
              body: JSON.stringify({
                systemPrompt: systemPrompt,
                userInput: finalUserInput,
              }),
            }
          );

          // Store the response for potential use outside the retry loop
          lastResponse = response;

          // Handle insufficient balance response (status code 402)
          if (response.status === 402) {
            const errorData = await response.json();
            throw {
              balance: 0,
              message:
                errorData.Error ||
                "Insufficient credits. Please recharge your account.",
            };
          }

          // If we get a 500 error, retry with exponential backoff
          if (response.status === 500) {
            retryCount++;
            if (retryCount === MAX_RETRIES) {
              throw new Error("MAX_RETRIES_REACHED");
            }

            // Show user-friendly message about retry attempt
            setErrorMessage(
              `We're experiencing high traffic. Retrying automatically... (Attempt ${retryCount} of ${MAX_RETRIES})`
            );
            setErrorDialogOpen(true);

            const backoffDelay =
              INITIAL_RETRY_DELAY * Math.pow(2, retryCount - 1);
            const jitter = Math.random() * 1000; // Add random jitter to prevent thundering herd
            await delay(backoffDelay + jitter);

            continue;
          }

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || "An error occurred");
          }

          // If we get here, the request was successful
          break;
        } catch (retryError) {
          if (retryError.balance === 0) {
            throw retryError; // Rethrow insufficient balance error
          }

          if (retryError.message === "MAX_RETRIES_REACHED") {
            throw new Error(
              "We're experiencing unusually high traffic. Please try again in a few minutes."
            );
          }

          if (retryCount === MAX_RETRIES - 1) {
            throw retryError;
          }

          retryCount++;
          console.error(`Error on retry ${retryCount}:`, retryError);
        }
      }

      // Process the successful response
      const result = await lastResponse.json();

      const completion = result; // Access the completion from our API response

      const content = completion?.choices?.[0]?.message?.content?.trim();

      if (type === "project") {
        const formattedContent = content.trim().replace(/\n{2,}/g, "\n");
        await setProjectMarkdown((prev) => [...prev, formattedContent]);
      } else {
        // Other tabs: treat response as JSON
        let jsonResponse;
        try {
          // Remove markdown code block syntax if present

          const cleanContent = content
            .replace(/```[\s\S]*?\n([\s\S]*?)```/g, "$1")
            .trim();

          jsonResponse = JSON.parse(cleanContent);
        } catch (parseError) {
          jsonResponse = null;
        }

        const jsonResponseNodes = jsonResponse["Nodes"];
        const jsonResponseEdges = jsonResponse["Edges"] || [];

        if (jsonResponseNodes && jsonResponseEdges.length > 0) {
          // Call positioning API when edges exist
          try {
            // Add these constants at the top of the file with other constants
            const POSITIONING_MAX_RETRIES = 5;
            const POSITIONING_INITIAL_RETRY_DELAY = 1000; // 1 second

            let retryCount = 0;
            let lastPositioningResponse;

            while (retryCount < POSITIONING_MAX_RETRIES) {
              try {
                const positioningResponse = await fetch(
                  `${process.env.REACT_APP_API_BASE_URL}/Handle_Gpt_API/chat`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${localStorage.getItem(
                        "clerk-token"
                      )}`,
                    },
                    body: JSON.stringify({
                      systemPrompt: prompts.positioning,
                      userInput: JSON.stringify({
                        Nodes: jsonResponseNodes,
                        Edges: jsonResponseEdges,
                      }),
                    }),
                  }
                );

                lastPositioningResponse = positioningResponse;

                // Handle insufficient balance response
                if (positioningResponse.status === 402) {
                  const errorData = await positioningResponse.json();
                  throw {
                    balance: 0,
                    message:
                      errorData.Error ||
                      "Insufficient credits for positioning calculation.",
                  };
                }

                // If we get a 500 error, retry with exponential backoff
                if (positioningResponse.status === 500) {
                  retryCount++;
                  if (retryCount === POSITIONING_MAX_RETRIES) {
                    throw new Error("MAX_RETRIES_REACHED");
                  }

                  // Show user-friendly message about retry attempt
                  setErrorMessage(
                    `Retrying node positioning... (Attempt ${retryCount} of ${POSITIONING_MAX_RETRIES})`
                  );
                  setErrorDialogOpen(true);

                  const backoffDelay =
                    POSITIONING_INITIAL_RETRY_DELAY *
                    Math.pow(2, retryCount - 1);
                  const jitter = Math.random() * 1000;
                  await new Promise((resolve) =>
                    setTimeout(resolve, backoffDelay + jitter)
                  );

                  continue;
                }

                if (!positioningResponse.ok) {
                  const error = await positioningResponse.json();
                  throw new Error(error);
                }

                // If we get here, the request was successful
                break;
              } catch (retryError) {
                if (retryError.balance === 0) {
                  throw retryError; // Rethrow insufficient balance error
                }

                if (retryError.message === "MAX_RETRIES_REACHED") {
                  throw new Error(
                    "Failed to calculate node positions after multiple attempts. Using default positioning."
                  );
                }

                if (retryCount === POSITIONING_MAX_RETRIES - 1) {
                  throw retryError;
                }

                retryCount++;
                console.error(
                  `Error on positioning retry ${retryCount}:`,
                  retryError
                );
              }
            }

            // Process the successful response
            const result = await lastPositioningResponse.json();
            const positioningCompletion = result;
            const positioningContent =
              positioningCompletion?.choices?.[0]?.message?.content?.trim();
            const cleanPositioningContent = positioningContent
              .replace(/```[\s\S]*?\n([\s\S]*?)```/g, "$1")
              .trim();
            const positioningData = JSON.parse(cleanPositioningContent);

            // Map the positions to the nodes
            if (jsonResponseNodes && setNodes) {
              const newNodes = jsonResponseNodes.map((nodeObj, index) => {
                const position = positioningData.nodePositions.find(
                  (pos) => pos.id === nodeObj.id
                );

                return {
                  id: nodeObj.id || `${type}-${Date.now()}-${index}`,
                  type: nodeObj.type || type,
                  sourcePosition: nodeObj.sourcePosition || Position.Right,
                  targetPosition: nodeObj.targetPosition || Position.Left,
                  position: position
                    ? { x: position.x, y: position.y }
                    : calculateNodePosition(index),
                  data: nodeObj.data || {
                    label: "Untitled",
                    description: "No description",
                  },
                };
              });
              responseNodes = newNodes;
              await setNodes((prev) => [...prev, newNodes]);
              // If you need to wait for the render cycle
              await new Promise((resolve) => setTimeout(resolve, 0));
            }
          } catch (positioningError) {
            console.error("Positioning Error:", positioningError);
            // Fall back to default positioning if the positioning API fails
            if (jsonResponseNodes && setNodes) {
              const newNodes = jsonResponseNodes.map((nodeObj, index) => ({
                id: nodeObj.id || `${type}-${Date.now()}-${index}`,
                type: nodeObj.type || type,
                sourcePosition: nodeObj.sourcePosition || Position.Right,
                targetPosition: nodeObj.targetPosition || Position.Left,
                position: calculateNodePosition(index),
                data: nodeObj.data || {
                  label: "Untitled",
                  description: "No description",
                },
              }));
              responseNodes = newNodes;
              await setNodes((prev) => [...prev, newNodes]);
              // If you need to wait for the render cycle
              await new Promise((resolve) => setTimeout(resolve, 0));
            }
          }
        } else if (jsonResponseNodes && setNodes) {
          // No edges, use default positioning
          const newNodes = jsonResponseNodes.map((nodeObj, index) => ({
            id: nodeObj.id || `${type}-${Date.now()}-${index}`,
            type: nodeObj.type || type,
            sourcePosition: nodeObj.sourcePosition || Position.Right,
            targetPosition: nodeObj.targetPosition || Position.Left,
            position: calculateNodePosition(index),
            data: nodeObj.data || {
              label: "Untitled",
              description: "No description",
            },
          }));
          responseNodes = newNodes;
          await setNodes((prev) => [...prev, newNodes]);
          // If you need to wait for the render cycle
          await new Promise((resolve) => setTimeout(resolve, 0));
        }

        // Map edges from the response
        if (jsonResponseEdges.length > 0 && setEdges) {
          const newEdges = jsonResponseEdges.map((edge) => ({
            id: edge.id || `e-${Date.now()}-${Math.random()}`,
            source: edge.source,
            target: edge.target,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#64748b", strokeWidth: 2 },
            labelStyle: {
              fill: "#64748b",
              fontSize: "10px",
              fontWeight: "bold",
            },
            label: edge.label || "relates to",
          }));
          responseEdges = newEdges;
          await setEdges((prev) => [...prev, newEdges]);
        }

        // Handle summary if present
        if (jsonResponse.Summary && setSummary) {
          await setSummary((prev) => {
            const newSummary = [...prev, jsonResponse.Summary];
            // Save immediately after state update
            return newSummary;
          });
        }
      }
      debouncedSave(() => completion);

      if(type != "dataModel")
        setIsLoading(false);

      if (isInitial) {
        if (type !== "project") {
          
          const tabNumber ={
            requirement: 1,
            userStory: 2,
            sitemap: 3,
            dataModel: 4,
          }

          setCurrentTab((prev) => tabNumber[type]);
        }
      }
      if (type == "dataModel") {
        await generateSQL(responseNodes , responseEdges);
      }
      await fetchCredits();
    } catch (error) {
      console.error("Error:", error);
      // Remove the last message that failed

      setMessages((prev) => prev.slice(0, -1));

      // Check if it's a balance error

      if (error.balance === 0) {
        setErrorMessage("Insufficient credits. Please recharge your account.");
      } else if (error.message.includes("high traffic")) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(
          "We apologize for the inconvenience. Our servers are currently experiencing high load. Please try again in a few moments."
        );
      }

      setErrorDialogOpen(true);

      if (error.balance === 0) {
        await fetchCredits();
      }

      setMessages((prev) => prev.slice(0, -1));
      if(type != "dataModel")
        setIsLoading(false);

    }
  };

  const handleProjectMessage = ({ text, isInitial }) =>
    handleMessage(
      text,
      "project",
      setProjectMessages,
      setProjectMarkdown,
      null,
      null,
      isInitial
    );
  const handleRequirementMessage = ({ text, isInitial }) =>
    handleMessage(
      text,
      "requirement",
      setRequirementMessages,
      setRequirementNodes,
      setRequirementEdges,
      setRequirementSummary,
      isInitial
    );
  const handleUserStoryMessage = ({ text, isInitial }) =>
    handleMessage(
      text,
      "userStory",
      setUserStoryMessages,
      setUserStoryNodes,
      setUserStoryEdges,
      setUserStorySummary,
      isInitial
    );
  const handleSitemapMessage = ({ text, isInitial }) =>
    handleMessage(
      text,
      "sitemap",
      setSitemapMessages,
      setSitemapNodes,
      setSitemapEdges,
      setSitemapSummary,
      isInitial
    );
  const handleDataModelMessage = ({ text, isInitial }) =>
    handleMessage(
      text,
      "dataModel",
      setDataModelMessages,
      setDataModelNodes,
      setDataModelEdges,
      setDataModelSummary,
      isInitial
    );

  const handleEditMessage = (type, index) => {
    const handleMessage = {
      project: handleProjectMessage,
      requirement: handleRequirementMessage,
      userStory: handleUserStoryMessage,
      sitemap: handleSitemapMessage,
      dataModel: handleDataModelMessage,
    };

    const messageSetters = {
      project: setProjectMessages,
      requirement: setRequirementMessages,
      userStory: setUserStoryMessages,
      sitemap: setSitemapMessages,
      dataModel: setDataModelMessages,
    };

    const messages = {
      project: projectMessages,
      requirement: requirementMessages,
      userStory: userStoryMessages,
      sitemap: sitemapMessages,
      dataModel: dataModelMessages,
    };

    setCurrentEdit({
      type,
      index,
      text: messages[type][index].text,
      setter: messageSetters[type],
      handleMessage: handleMessage[type],
      handleDeleteMessage: handleDeleteMessage,
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async (newText) => {
    if (!currentEdit) return;

    // First delete the old message
    await currentEdit.handleDeleteMessage(currentEdit.type, currentEdit.index);

    // Then send the new message
    if (currentEdit.index === 0) {
      currentEdit.handleMessage({ text: newText, isInitial: true });
    } else {
      currentEdit.handleMessage({ text: newText });
    }

    setEditDialogOpen(false);
    setCurrentEdit(null);
  };

  const handleDeleteMessage = (type, index) => {
    const messageSetters = {
      project: setProjectMessages,
      requirement: setRequirementMessages,
      userStory: setUserStoryMessages,
      sitemap: setSitemapMessages,
      dataModel: setDataModelMessages,
    };

    const setNodes = {
      project: setProjectMarkdown,
      requirement: setRequirementNodes,
      userStory: setUserStoryNodes,
      sitemap: setSitemapNodes,
      dataModel: setDataModelNodes,
    };

    const setEdges = {
      requirement: setRequirementEdges,
      userStory: setUserStoryEdges,
      sitemap: setSitemapEdges,
      dataModel: setDataModelEdges,
    };

    const setSummaries = {
      requirement: setRequirementSummary,
      userStory: setUserStorySummary,
      sitemap: setSitemapSummary,
      dataModel: setDataModelSummary,
    };

    setNodes[type]((prev) => {
      return prev.slice(0, index);
    });

    if (type === "sitemap" || type === "dataModel") {
      setEdges[type]((prev) => {
        return prev.slice(0, index);
      });
    }
    if (
      type === "requirement" ||
      type === "userStory" ||
      type === "sitemap" ||
      type === "dataModel"
    ) {
      setSummaries[type]((prev) => {
        return prev.slice(0, index);
      });
    }
    messageSetters[type]((prev) => prev.slice(0, index));
  };

  function convertRequirementsToText(requirements) {
    return requirements
      .map((req) => {
        const label = req.data.label;
        const description = req.data.description;
        return `${label}: ${description}`;
      })
      .join("\n");
  }

  const extractUserStories = (stories) => {
    return stories
      .map(
        (story) =>
          `As a ${story.data.role}, I want to ${story.data.want}, so that ${story.data.benefit}.`
      )
      .join("\n");
  };

  const handleNextStepFromDescription = async () => {
    const latestDescription = projectMarkdown[projectMarkdown.length - 1];
    // Clear requirement states
    setRequirementNodes((prev) => []);
    setRequirementEdges((prev) => []);
    setRequirementMessages((prev) => []);
    setRequirementSummary((prev) => []);
    // Change tab and generate new requirements
    await handleRequirementMessage({
      text: latestDescription,
      isInitial: true,
    });
    setCurrentTab(1);
  };

  const handleNextStepFromRequirements = async () => {
    const latestRequirementMessage = convertRequirementsToText(
      requirementNodes[requirementNodes.length - 1]
    );
    if (latestRequirementMessage) {
      // Clear user story states
      setUserStoryNodes((prev) => []);
      setUserStoryEdges((prev) => []);
      setUserStoryMessages((prev) => []);
      setUserStorySummary((prev) => []);
      // Change tab and generate new user stories
      await handleUserStoryMessage({
        text: latestRequirementMessage,
        isInitial: true,
      });
      setCurrentTab(2);
    }
  };

  const handleNextStepFromUserStories = async () => {
    const prompt =
      "Requirements: " +
      convertRequirementsToText(requirementNodes[requirementNodes.length - 1]) +
      " User Stories: " +
      extractUserStories(userStoryNodes[userStoryNodes.length - 1]);
    if (prompt) {
      // Clear sitemap states
      setSitemapNodes((prev) => []);
      setSitemapEdges((prev) => []);
      setSitemapMessages((prev) => []);
      setSitemapSummary((prev) => []);
      // Change tab and generate new sitemap
      await handleSitemapMessage({ text: prompt, isInitial: true });
      setCurrentTab(3);
    }
  };

  const handleNextStepFromSitemap = async () => {
    const prompt =
      "Description: " +
      projectMarkdown[projectMarkdown.length - 1] +
      " Requirements: " +
      convertRequirementsToText(requirementNodes[requirementNodes.length - 1]) +
      " User Stories: " +
      extractUserStories(userStoryNodes[userStoryNodes.length - 1]);
    if (prompt) {
      // Clear data model states
      setDataModelNodes((prev) => []);
      setDataModelEdges((prev) => []);
      setDataModelMessages((prev) => []);
      setDataModelSummary((prev) => []);
      // Change tab and generate new data models
      await handleDataModelMessage({ text: prompt, isInitial: true });
     
      setCurrentTab(4);
    }
  };

  // Add this new error dialog component
  const ErrorDialog = ({ open, onClose, message }) => (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Error</DialogTitle>
      <DialogContent>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // Add this function to generate SQL from data models
  const generateSQL = async (nodes , edges) => {
    setIsLoading(true);
    const freshToken = await getToken();
    localStorage.setItem("clerk-token", freshToken);
    // const nodes = dataModelNodes[dataModelNodes.length - 1];
    // const edges = dataModelEdges[dataModelEdges.length - 1];
  
    const systemPrompt = `Generate a complete SQL schema based on the provided data models and their relationships. Also include these columns in each and every table - isActive tinyint(1) NOT NULL DEFAULT '1',
  createdBy VARCHAR(255) NOT NULL,
  createdAt VARCHAR(255) NOT NULL,
  modifiedAt DATETIME NOT NULL,
  modifiedBy VARCHAR(255),
  Make sure you remove if there is similar column with different name serving same purpose.
   Follow these requirements:
    1. Use standard SQL syntax compatible with major databases
    2. Include proper foreign key constraints and indexes
    3. Create tables in correct order (referenced tables before tables with foreign keys)
    4. Use appropriate data types and constraints
    5. Include helpful comments for each table
    6. Add appropriate indexes for foreign keys and frequently queried fields
    7. Include timestamp fields (created_at, updated_at) for all tables
    8. Dont include things like "CHARACTER SET utf8 COLLATE utf8_general_ci" etc. Just the table definitions.
    Return only the SQL statements without any additional explanation.
    9. Add appropriate CASCADE rules for foreign keys
    10. Dont Include database creation statement
    11. Dont Include any sql comments as this script will be used for execution in database server.
    12. Keep it simple database design and dont indluce any index .
    `

    const finalUserInput = `Data Models:
    ${JSON.stringify(nodes, null, 2)}

    Relationships:
    ${JSON.stringify(edges, null, 2)}

    Please generate a complete SQL schema following the requirements in the system prompt.`;

    try {
      // Add retry logic with exponential backoff
      const MAX_RETRIES = 5;
      const INITIAL_RETRY_DELAY = 1000; // 1 second
      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
      let retryCount = 0;
      let lastResponse;

      while (retryCount < MAX_RETRIES) {
        try {
          const freshToken = await getToken();
          localStorage.setItem("clerk-token", freshToken);
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/Handle_Gpt_API/chat`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("clerk-token")}`,
              },
              body: JSON.stringify({
                systemPrompt: systemPrompt,
                userInput: finalUserInput,
              }),
            }
          );

          // Store the response for potential use outside the retry loop
          lastResponse = response;

          // Handle insufficient balance response (status code 402)
          if (response.status === 402) {
            const errorData = await response.json();
            throw {
              balance: 0,
              message:
                errorData.Error ||
                "Insufficient credits. Please recharge your account.",
            };
          }

          // If we get a 500 error, retry with exponential backoff
          if (response.status === 500) {
            retryCount++;
            if (retryCount === MAX_RETRIES) {
              throw new Error("MAX_RETRIES_REACHED");
            }

            // Show user-friendly message about retry attempt
            setErrorMessage(
              `We're experiencing high traffic. Retrying automatically... (Attempt ${retryCount} of ${MAX_RETRIES})`
            );
            setErrorDialogOpen(true);

            const backoffDelay =
              INITIAL_RETRY_DELAY * Math.pow(2, retryCount - 1);
            const jitter = Math.random() * 1000; // Add random jitter to prevent thundering herd
            await delay(backoffDelay + jitter);

            continue;
          }

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || "An error occurred");
          }

          // If we get here, the request was successful
          break;
        } catch (retryError) {
          if (retryError.balance === 0) {
            throw retryError; // Rethrow insufficient balance error
          }

          if (retryError.message === "MAX_RETRIES_REACHED") {
            throw new Error(
              "We're experiencing unusually high traffic. Please try again in a few minutes."
            );
          }

          if (retryCount === MAX_RETRIES - 1) {
            throw retryError;
          }

          retryCount++;
          console.error(`Error on retry ${retryCount}:`, retryError);
        }
      }

      // Process the successful response
      const result = await lastResponse.json();
      const completion = result;
      const sql = completion?.choices?.[0]?.message?.content?.trim();
     

      // Format SQL with consistent indentation and spacing, without markdown
      const formattedSQL = sql
        .replace(/```sql/g, "")
        .replace(/```/g, "")
        .split(";")
        .map((statement) => statement.trim())
        .filter((statement) => statement.length > 0)
        .map((statement) => `${statement};`)
        .join("\n\n")
        .trim();

      setGeneratedSQL(() => formattedSQL);
      await saveUnifiedData(() => completion);
      await fetchCredits();
    } catch (error) {
      console.error("Error:", error);

      // Check if it's a balance error
      if (error.balance === 0) {
        setErrorMessage("Insufficient credits. Please recharge your account.");
      } else if (error.message.includes("high traffic")) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(
          "We apologize for the inconvenience. Our servers are currently experiencing high load. Please try again in a few moments."
        );
      }

      setErrorDialogOpen(true);

      if (error.balance === 0) {
        await fetchCredits();
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Add these new states near your other state declarations
  const [deploymentStatus, setDeploymentStatus] = useState("not_started"); // possible values: 'not_started', 'deploying', 'deployed', 'failed'
  const [pollingInterval, setPollingInterval] = useState(null);

  // Add these constants at the top of the file
  const DEPLOYMENT_CHECK_INTERVAL = 2 * 60 * 1000; // Check every 2 minutes
  const DEPLOYMENT_TIMEOUT = 20 * 60 * 1000; // Total timeout of 20 minutes
  const INITIAL_CHECK_DELAY = 10 * 60 * 1000; // First check after 10 minutes

  // const startPolling = async (projectId) => {
  //   // Clear any existing polling interval
  //   if (pollingInterval) {
  //     clearInterval(pollingInterval);
  //   }

  // Set initial deployment status
  // setDeploymentStatus("deploying");
  // setErrorMessage("Deployment started. This process typically takes 15-20 minutes. You'll be notified when it's ready.");
  // setErrorDialogOpen(true);

  // Store the deployment start time
  // const deploymentStartTime = Date.now();

  // // Perform initial check after 10 minutes
  // const initialCheckTimeout = setTimeout(async () => {
  //   await checkDeploymentStatus(projectId, deploymentStartTime);
  // }, INITIAL_CHECK_DELAY);

  // // Start periodic checking after initial delay
  // const interval = setInterval(async () => {
  //   await checkDeploymentStatus(projectId, deploymentStartTime);
  // }, DEPLOYMENT_CHECK_INTERVAL);

  //   setPollingInterval(interval);

  //   // Set timeout to stop polling after maximum deployment time
  //   setTimeout(() => {
  //     clearInterval(interval);
  //     clearTimeout(initialCheckTimeout);
  //     setPollingInterval(null);

  //     // Only update status if still deploying
  //     setDeploymentStatus((prevStatus) => {
  //       if (prevStatus === "deploying") {
  //         // setErrorMessage("Deployment timed out. Please try again or contact support if the issue persists.");
  //         // setErrorDialogOpen(true);
  //         return "failed";
  //       }
  //       return prevStatus;
  //     });
  //   }, DEPLOYMENT_TIMEOUT);
  // };

  // const checkDeploymentStatus = async (projectId, startTime) => {
  //   try {
  //     const [linksResponse, downloadResponse] = await Promise.all([
  //       getProjectLinksService(projectId),
  //       getProjectDownloadUrlsService(projectId),
  //     ]);

  //     // Calculate elapsed time
  //     const elapsedTime = Date.now() - startTime;

  //     if (
  //       linksResponse.data?.status === "completed" &&
  //       downloadResponse.data?.status === "completed"
  //     ) {
  //       setDeploymentStatus("deployed");
  //       clearInterval(pollingInterval);
  //       setPollingInterval(null);

  //       // Extract and set URLs
  //       const { serverUrl, designerUrl, previewUrl } = linksResponse.data;
  //       setApiUrls({
  //         SERVER: `${serverUrl}/swagger/index.html`,
  //         DESIGNER: `${designerUrl}/dnd/dndBuilder/1`,
  //         PREVIEW: `${previewUrl}/dnd/dndBuilder/preview?id=2`,
  //       });

  //       const minutes = Math.floor(elapsedTime / 60000);
  //       // setErrorMessage(`Deployment completed successfully in ${minutes} minutes! Your API is now ready to use.`);
  //       // setErrorDialogOpen(true);
  //     } else if (
  //       linksResponse.data?.status === "failed" ||
  //       downloadResponse.data?.status === "failed"
  //     ) {
  //       setDeploymentStatus("failed");
  //       clearInterval(pollingInterval);
  //       setPollingInterval(null);

  //       // setErrorMessage("Deployment failed. Please try again or contact support if the issue persists.");
  //       // setErrorDialogOpen(true);
  //     }
  //   } catch (error) {
  //     console.error("Error checking deployment status:", error);
  //     // Don't stop polling on network errors
  //   }
  // };

  // Add this effect to handle initial status check when returning to the app

  const handleNextStepFromDataModels = async () => {
    try {
      let selected_project_id = selectedProject;
      let project_name = projects.find(
        (project) => project.project_id === selected_project_id
      ).project_name;

      setIsLoading(true);
      setStatus(0);
      const freshToken = await getToken();
      localStorage.setItem("clerk-token", freshToken);
      var response = await codeGeneratorService(
        selected_project_id,
        project_name,
        generatedSQL
      );
      console.log("next from data");
      console.log(response);
      if (response.data.error && response.data.balance == 0) {
        setErrorMessage(response.data.error);
        setErrorDialogOpen(true);
      }
      fetchStatus();
    } catch (error) {
      console.error("Error initiating deployment:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Clean up polling on component unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Update the deployment button rendering
  const getDeployButtonText = (notUsingThisStatus) => {
    let curStatus = status;
    switch (curStatus) {
      case 4:
        return "Deploy";
      case 0:
        return "Deploying...";
      case 1:
        return "Redeploy";
      case 2:
        return "Retry Deployment";
      case 3:
        return "Retry Deployment";
      default:
        return "Deploy";
    }
  };

  const [loading, setLoading] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Add toggle handler
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  const SIDEBAR_WIDTH = 240;
  const COLLAPSED_SIDEBAR_WIDTH = 56;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [newProjectDialogOpen, setNewProjectDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");

  const [isLoadingProjects, setIsLoadingProjects] = useState(true); // Changed default to true
  const { isSignedIn } = useAuth();
  const [credits, setCredits] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const { getToken } = useAuth();
  const { user, isLoaded } = useUser();
  // Add a new state to track initial loading
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const PROJECT_TEMPLATES = [
    // Existing Core Templates
    {
      name: "CRM System",
      description:
        "A comprehensive Customer Relationship Management system with contact management, deal tracking, and communication history. Key features include lead management, sales pipeline tracking, customer communication logs, task management, and reporting dashboard.",
      icon: "👥",
      industry: "Cross-Industry",
    },
    {
      name: "Bookkeeping System",
      description:
        "A financial management system for small to medium businesses. Features include expense tracking, invoice generation, financial reporting, bank reconciliation, and budget management. The system should support multiple currencies and tax calculations.",
      icon: "📊",
      industry: "Cross-Industry",
    },
    {
      name: "Patient Intake Portal",
      description:
        "A healthcare portal for streamlining patient intake process. Include features for appointment scheduling, medical history collection, insurance verification, digital form completion, and secure messaging between patients and healthcare providers.",
      icon: "🏥",
      industry: "Healthcare",
    },
    {
      name: "Risk Analysis Tool",
      description:
        "An enterprise risk management tool for identifying, assessing, and monitoring business risks. Features include risk assessment matrices, mitigation planning, compliance tracking, incident reporting, and risk analytics dashboard.",
      icon: "⚠️",
      industry: "Cross-Industry",
    },

    // Healthcare Industry
    {
      name: "Medical Inventory Management",
      description:
        "An AI-driven inventory monitoring system for healthcare supplies with demand forecasting, automated restocking alerts, expiry tracking, and usage analytics to ensure optimal stock levels and prevent shortages.",
      icon: "🏥",
      industry: "Healthcare",
    },
    {
      name: "Clinical Trial Management",
      description:
        "A comprehensive system for managing clinical trials including participant tracking, document management, compliance reporting, data collection, and analysis tools with regulatory compliance features.",
      icon: "🔬",
      industry: "Healthcare",
    },
    {
      name: "Doctor-Patient Communication Portal",
      description:
        "A secure messaging platform for continuous and HIPAA-compliant doctor-patient interactions, featuring appointment scheduling, prescription renewals, and secure file sharing.",
      icon: "💬",
      industry: "Healthcare",
    },
    {
      name: "Medical Billing System",
      description:
        "Automated medical coding and billing system with error detection, real-time claims tracking, and insurance verification features.",
      icon: "💵",
      industry: "Healthcare",
    },

    // Financial Services
    {
      name: "Loan Application Tracker",
      description:
        "A system to manage and automate loan applications with real-time status updates, document verification, credit scoring integration, and automated approval workflows.",
      icon: "💰",
      industry: "Financial",
    },
    {
      name: "Investment Portfolio Manager",
      description:
        "A portfolio management tool offering real-time tracking, analytics, rebalancing suggestions, and performance reporting with risk assessment features.",
      icon: "📈",
      industry: "Financial",
    },
    {
      name: "KYC Verification System",
      description:
        "Automated Know Your Customer verification system with document uploads, validation, and compliance monitoring for financial institutions.",
      icon: "🔍",
      industry: "Financial",
    },
    {
      name: "Fraud Detection System",
      description:
        "AI-powered fraud detection system with real-time transaction monitoring, anomaly detection, and automated alert mechanisms.",
      icon: "🚨",
      industry: "Financial",
    },

    // Manufacturing
    {
      name: "Production Line Monitor",
      description:
        "A live dashboard for tracking production KPIs, identifying bottlenecks, analyzing downtime, and monitoring quality metrics with predictive maintenance alerts.",
      icon: "🏭",
      industry: "Manufacturing",
    },
    {
      name: "Supply Chain Manager",
      description:
        "A comprehensive supply chain management system with real-time tracking, supplier performance monitoring, inventory optimization, and disruption alerts.",
      icon: "🔄",
      industry: "Manufacturing",
    },
    {
      name: "Quality Control System",
      description:
        "Automated quality inspection and control system with defect tracking, statistical process control, and compliance reporting features.",
      icon: "✅",
      industry: "Manufacturing",
    },
    {
      name: "Equipment Maintenance Scheduler",
      description:
        "Predictive maintenance scheduling system based on equipment health metrics, usage data, and maintenance history.",
      icon: "🔧",
      industry: "Manufacturing",
    },

    // Retail
    {
      name: "Inventory Management System",
      description:
        "Real-time inventory tracking system with automated reordering, sales forecasting, and analytics for optimal stock levels across multiple locations.",
      icon: "🏪",
      industry: "Retail",
    },
    {
      name: "Customer Loyalty Platform",
      description:
        "A complete loyalty program management system with points tracking, rewards automation, customer segmentation, and campaign management tools.",
      icon: "⭐",
      industry: "Retail",
    },
    {
      name: "Store Performance Analytics",
      description:
        "Comprehensive analytics dashboard for tracking store-level metrics, sales performance, and customer behavior patterns.",
      icon: "📊",
      industry: "Retail",
    },
    {
      name: "Price Optimization Tool",
      description:
        "AI-driven pricing analysis system with competitor monitoring, demand forecasting, and automated price adjustment features.",
      icon: "💲",
      industry: "Retail",
    },

    // Insurance
    {
      name: "Claims Management System",
      description:
        "A centralized system for claim submission, tracking, and settlement with automated workflows. Includes fraud detection, document management, and status tracking features.",
      icon: "📝",
      industry: "Insurance",
    },
    {
      name: "Insurance Product Manager",
      description:
        "A comprehensive tool for managing insurance products, pricing, and policy lifecycle. Features include risk assessment, premium calculation, and policy document generation.",
      icon: "🏦",
      industry: "Insurance",
    },
    {
      name: "Policy Renewal Manager",
      description:
        "Automated system for tracking policy expirations, generating renewal notices, and managing policy updates with customer communications.",
      icon: "🔄",
      industry: "Insurance",
    },

    // Construction
    {
      name: "Construction Project Tracker",
      description:
        "Comprehensive project management system for construction with resource planning, progress tracking, and compliance monitoring. Includes document management and safety reporting.",
      icon: "🏗️",
      industry: "Construction",
    },
    {
      name: "Site Safety Management Tool",
      description:
        "Mobile-enabled platform for construction site safety management with incident reporting, inspection scheduling, and compliance tracking features.",
      icon: "⚠️",
      industry: "Construction",
    },
    {
      name: "Equipment Tracking System",
      description:
        "Real-time tracking system for construction equipment with maintenance scheduling, utilization monitoring, and location tracking.",
      icon: "🚜",
      industry: "Construction",
    },

    // Legal Services
    {
      name: "Legal Case Management",
      description:
        "End-to-end case management system with document control, deadline tracking, and client communication features. Includes time tracking and billing integration.",
      icon: "⚖️",
      industry: "Legal",
    },
    {
      name: "Contract Review System",
      description:
        "AI-assisted contract review and management platform with version control and approval workflows. Features template management and clause analysis.",
      icon: "📄",
      industry: "Legal",
    },
    {
      name: "Legal Research Platform",
      description:
        "Comprehensive legal research management system with case law database, document organization, and citation tracking.",
      icon: "📚",
      industry: "Legal",
    },

    // Food & Beverage
    {
      name: "Restaurant Management System",
      description:
        "Complete restaurant operations platform with table management, order processing, and inventory control. Includes POS integration, staff scheduling, and analytics dashboard.",
      icon: "🍽️",
      industry: "Food & Beverage",
    },
    {
      name: "Food Safety Compliance Tool",
      description:
        "Automated compliance tracking system for food safety regulations with inspection checklists and audit trails. Features temperature monitoring and incident reporting.",
      icon: "✅",
      industry: "Food & Beverage",
    },
    {
      name: "Kitchen Inventory Manager",
      description:
        "Real-time kitchen inventory tracking system with recipe costing, waste management, and automated ordering features.",
      icon: "🥘",
      industry: "Food & Beverage",
    },
  ];
  // Add this state near your other state declarations
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredTemplates, setFilteredTemplates] = useState(PROJECT_TEMPLATES);

  // Add this effect to filter templates when search query changes
  useEffect(() => {
    const filtered = PROJECT_TEMPLATES.filter(
      (template) =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredTemplates(filtered);
  }, [searchQuery]);

  // Modify the project fetching useEffect
  useEffect(() => {
    const initializeApp = async () => {
      if (isSignedIn) {
        try {
          await fetchProjects();
          setInitialLoadComplete(true);
          await fetchCredits();
        } catch (error) {
          console.error("Error during initialization:", error);
          setInitialLoadComplete(true); // Still set to true so we can show error state
        }
      }
    };

    if (isSignedIn) {
      initializeApp();
    }
  }, [isSignedIn]);

  useEffect(() => {
    const setClerkToken = async () => {
      const token = await getToken();

      localStorage.setItem("clerk-token", token);
    };

    setClerkToken();
  }, []); // Run once when component mounts

  const fetchProjects = async () => {
    setIsLoadingProjects(true);

    try {
      const freshToken = await getToken();
      localStorage.setItem("clerk-token", freshToken);
      const response = await getAllProjects(1, 100);
      // Check if response matches the expected structure
      if (response?.data?.code === 1 && response?.data?.document?.records) {
        const formattedProjects = response.data.document.records.map(
          (project) => ({
            project_id: project.project_id,
            project_name: project.project_name,
            description: project.project_description || "",
            createdAt: project.createdAt,
            createdBy: project.createdBy,
            modifiedAt: project.modifiedAt,
            modifiedBy: project.modifiedBy,
            isActive: Boolean(project.isActive), // Convert to boolean if it's coming as 1/0
          })
        );

        setProjects(formattedProjects);
      } else {
        console.error("Invalid or empty response:", response);
        setProjects([]);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setErrorMessage("Failed to load projects. Please try again.");
      setErrorDialogOpen(true);
    } finally {
      setIsLoadingProjects(false);
    }
  };
  const fetchCredits = async (retryCount = 0, maxRetries = 3, delay = 2000) => {
    setCreditsLoading(true);
    const freshToken = await getToken();
    localStorage.setItem("clerk-token", freshToken);

    try {
      const response = await getAllCredits(1, 100);

      // Check if we have valid data
      if (
        response?.data?.code === 1 &&
        response?.data?.document?.records?.length > 0
      ) {
        const creditInfo = response.data.document.records[0];
        setCredits({
          amount: creditInfo.amount,
          expiryDate: new Date(creditInfo.expiry_date),
        });
        return; // Success - exit function
      }

      // If we haven't reached max retries and don't have data yet, retry
      if (retryCount < maxRetries) {
        console.log(
          `Retrying credits fetch... Attempt ${retryCount + 1} of ${maxRetries}`
        );

        // Wait for specified delay
        await new Promise((resolve) => setTimeout(resolve, delay));

        // Recursive call with incremented retry count
        return fetchCredits(retryCount + 1, maxRetries, delay);
      }

      // If we've exhausted retries, throw an error
      throw new Error("Failed to fetch credits after multiple attempts");
    } catch (error) {
      console.error("Error fetching credits:", error);
      //  setErrorMessage('Failed to load credits information');
      //  setErrorDialogOpen(true);
    } finally {
      setCreditsLoading(false);
    }
  };

  // Update useEffect to handle loading state
  useEffect(() => {
    if (isSignedIn) {
      // Assuming you have access to isSignedIn from Clerk

      fetchProjects();
    }
  }, [isSignedIn]);
  useEffect(() => {
    if (isSignedIn) {
      fetchCredits();
    }
  }, [isSignedIn]);

  const handleCreateProject = async (projectName) => {
    if (projectName.trim()) {
      setLoading(true);
      try {
        const now = await new Date().toISOString();
        const projectData = {
          project_name: projectName.trim(),
          project_description: "",
          isActive: 1,
          createdAt: now,
          modifiedAt: now,
          modifiedBy: "current_user",
        };
        const freshToken = await getToken();
        localStorage.setItem("clerk-token", freshToken);
        const response = await addProjects(projectData);

        if (response?.data?.code === 1 && response?.data?.document) {
          const createdProject = {
            project_id: response.data.document,
            ...projectData,
          };

          setProjects((prevProjects) => [...prevProjects, createdProject]);
          setNewProjectDialogOpen(() => false);
          setSelectedProject(() => createdProject.project_id);
          setSelectedProjectName(() => createdProject.project_name);
          setEveryStateToEmpty();
          setCurrentTab(0);
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (error) {
        console.error("Failed to create project:", error);
        setErrorMessage("Failed to create project. Please try again.");
        setErrorDialogOpen(true);
      } finally {
        setLoading(false);
      }
    }
  };

  // Common Grid container styles
  const gridContainerStyles = {
    spacing: { xs: 2, md: 4 },
    className: "h-full",
    sx: {
      height: "calc(100vh - 120px)", // Account for header/tabs height
      mt: 2, // Add some margin from the tabs
    },
  };

  // Common left column Grid item styles
  const leftColumnStyles = {
    xs: 12,
    md: 4,
    sx: {
      height: "100%", // Take full height of container
      "& > .MuiPaper-root": {
        // Target the Paper component inside
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
  };

  // Common right column Grid item styles
  const rightColumnStyles = {
    xs: 12,
    md: 8,
    sx: {
      height: "100%", // Take full height of container
      "& > .MuiPaper-root": {
        // Target the Paper component inside
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
  };

  const rightColumnStylesDeployment = {
    xs: 12,
    md: 12,
    sx: {
      height: "100%", // Take full height of container
      "& > .MuiPaper-root": {
        // Target the Paper component inside
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
  };

  // Add this effect near the top of your App component
  useEffect(() => {
    // Load Stripe Buy Button script
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/buy-button.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Add these predefined project templates

  const NewProjectPage = ({ onSubmit, addNewPrompt, loading }) => {
    const [projectData, setProjectData] = useState({
      name: "",
      description: "",
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      await onSubmit(projectData.name);
      await addNewPrompt({ text: projectData.description, isInitial: true });
    };

    const handleChange = (e) => {
      const { name, value } = e.target;
      setProjectData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleTemplateSelect = (template) => {
      setProjectData({
        name: template.name,
        description: template.description,
      });
    };

    return (
      <>
      <><ClarityAnalytics /></>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="w-full max-w-7xl mx-auto px-4 py-8 flex-grow">
          <div className="mb-10">
            <h1 className="text-3xl font-bold text-gray-900">
              Create New Project
            </h1>
            <p className="mt-2 text-gray-600">
              Start from scratch or use one of our templates
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Form Section */}
            <div className="space-y-6">
              <form onSubmit={handleSubmit}>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="space-y-6">
                      <div>
                        <label
                          htmlFor="projectName"
                          className="block text-sm font-medium text-gray-900"
                        >
                          Project Name
                        </label>
                        <input
                          id="projectName"
                          name="name"
                          type="text"
                          required
                          value={projectData.name}
                          onChange={handleChange}
                          disabled={loading}
                          className="mt-2 block w-full rounded-lg border border-gray-200 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 disabled:bg-gray-50 text-base"
                          placeholder="Enter your project name"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="projectDescription"
                          className="block text-sm font-medium text-gray-900"
                        >
                          Description
                        </label>
                        <textarea
                          id="projectDescription"
                          name="description"
                          value={projectData.description}
                          onChange={handleChange}
                          disabled={loading}
                          rows={12}
                          className="mt-2 block w-full rounded-lg border border-gray-200 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 disabled:bg-gray-50 text-base resize-none"
                          placeholder="Describe your project..."
                        />
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="flex justify-end space-x-4">
                      <button
                        type="button"
                        onClick={() => window.history.back()}
                        disabled={loading}
                        className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:text-gray-400"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading || !projectData.name.trim()}
                        className="px-4 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed min-w-[100px]"
                      >
                        {loading ? (
                          <div className="flex items-center justify-center">
                            <svg
                              className="animate-spin h-5 w-5"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                                fill="none"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                          </div>
                        ) : (
                          "Create"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Templates Section */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <div className="flex flex-col space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold text-gray-900">
                        Quick Start Templates
                      </h2>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search templates..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                        <svg
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                    </div>

                    <div
                      className="space-y-3 overflow-y-auto max-h-[calc(100vh-400px)] pr-2 scroll-smooth"
                      style={{
                        scrollbarWidth: "thin",
                        scrollbarColor: "#CBD5E1 transparent",
                      }}
                    >
                      {filteredTemplates.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          No templates match your search
                        </div>
                      ) : (
                        filteredTemplates.map((template, index) => (
                          <button
                            key={index}
                            onClick={() => handleTemplateSelect(template)}
                            className="w-full flex items-center p-4 border border-gray-200 rounded-xl hover:border-gray-900 hover:bg-gray-50 transition-all duration-200 text-left group"
                          >
                            <span className="text-2xl mr-4 flex-shrink-0">
                              {template.icon}
                            </span>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-900 group-hover:text-gray-900">
                                {template.name}
                              </h3>
                              <p className="mt-1 text-sm text-gray-500 line-clamp-2 group-hover:text-gray-600">
                                {template.description}
                              </p>
                            </div>
                            <svg
                              className="w-5 h-5 text-gray-400 group-hover:text-gray-900 flex-shrink-0 ml-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  };

  // const [selectProjectDialogOpen, setSelectProjectDialogOpen] = useState(false);
  const [dataSideTab, setdataSideTab] = useState(0);

  const handleRightTabChange = (event, newValue) => {
    setdataSideTab(newValue);
  };

  // 0 -> loading (pending )

  // 1 -> deployed succ

  // 2 -> Failed

  // 3 -> Timeout

  // 4 -> no entry (not initiated)

  const getPopupMessage = (status) => {
    switch (status) {
      case 0:
        return "Deployment started. This process typically takes 15-20 minutes. You'll be notified when it's ready.";
      case 1:
        return "Deployment completed successfully! Your web app is now ready to use.";
      case 2:
        return "Last deployment failed. Please try again or contact support if the issue persists.";
      case 3:
        return "Timeout, please redeploy.";
      default:
        return "Please wait";
    }
  };

  const [status, setStatus] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const fetchStatus = async () => {
    if (currentTab == 4) {
      try {
        console.log("Fetching status for project:", selectedProject);
        const freshToken = await getToken();
        localStorage.setItem("clerk-token", freshToken);
        const response = await getCodeGeneratorStatusService(selectedProject);
        console.log("API Response:", response.data);

        const newStatus = response.data.status;
        setStatus(newStatus);

        // Show popup for the initial status
        setShowPopup(true);
      } catch (error) {
        console.error("Error fetching API status:", error);
      }
    }
  };

 
  // Fetch status when Tab 4 is opened
  useEffect(() => {
    if (currentTab == 4) {
      fetchStatus(); 
      
    }
  }, [currentTab ]);

  const closePopup = async() => {
    if(status == 1)
    {
      const freshToken = await getToken();
      localStorage.setItem("clerk-token", freshToken);
    }
    setShowPopup(false);
  };

  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);

  // Add this state near your other state declarations
  const [iframeKey, setIframeKey] = useState(1);

  // Add these constants near the top of your file, with other constants
  const [apiUrls, setApiUrls] = useState({
    SERVER: "",
    DESIGNER: "",
    PREVIEW: "",
  });

  // Add new component for the video tutorial modal
  const TutorialModal = ({ open, onClose, videoUrl, title }) => (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          bgcolor: "background.paper",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 2,
        }}
      >
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            position: "relative",
            paddingTop: "56.25%" /* 16:9 aspect ratio */,
          }}
        >
          <iframe
            src={videoUrl}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: 0,
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Box>
      </DialogContent>
    </Dialog>
  );

  // Add this near your other state declarations
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [currentTutorial, setCurrentTutorial] = useState({
    url: "",
    title: "",
  });

  // Add tutorial data
  const TUTORIALS = {
    server: {
      url: "https://www.youtube.com/embed/your_server_tutorial_id",
      title: "How to Use the API Server",
    },
    designer: {
      url: "https://www.youtube.com/embed/your_designer_tutorial_id",
      title: "How to Use the API Designer",
    },
    preview: {
      url: "https://www.youtube.com/embed/your_preview_tutorial_id",
      title: "How to Use the API Preview",
    },
  };

  // Update the tab labels to include help buttons
  const TabLabel = ({ label, tutorialKey }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <span>{label}</span>
      <Box sx={{ display: "flex", ml: 1 }}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setIframeKey((prev) => prev + 1);
          }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            window.open(apiUrls[tutorialKey], "_blank");
          }}
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setCurrentTutorial(TUTORIALS[tutorialKey]);
            setTutorialOpen(true);
          }}
          sx={{
            "&:hover": {
              color: "primary.main",
            },
          }}
        >
          <PlayCircleOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  // Add this new component
  const DemoDialog = ({ open, onClose }) => (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          bgcolor: 'background.paper',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.7)',
            },
            zIndex: 1
          }}
        >
          <CloseIcon />
        </IconButton>
        <div style={{
          position: 'relative',
          boxSizing: 'content-box',
          maxHeight: '80vh',
          width: '100%',
          aspectRatio: '2.157303370786517',
          padding: '40px 0 40px 0'
        }}>
          <iframe 
            src="https://app.supademo.com/embed/cm6an1etj0l99280i1u6yoibl?embed_v=2"
            loading="lazy"
            title="how to use portal"
            allow="clipboard-write"
            frameBorder="0"
            webkitallowfullscreen="true"
            mozallowfullscreen="true"
            allowFullScreen
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
          />
        </div>
      </DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: 'background.paper' }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: 'grey.900',
            '&:hover': {
              bgcolor: 'grey.800',
            }
          }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Add this new state near your other state declarations
  const [showDemoDialog, setShowDemoDialog] = useState(false);

  // Add this effect to handle the demo dialog
  useEffect(() => {
    const hasSeenDemo = localStorage.getItem('hasSeenDemo');
    if (!hasSeenDemo) {
      setShowDemoDialog(true);
    }
  }, []);

  // Add this handler
  const handleCloseDemoDialog = () => {
    localStorage.setItem('hasSeenDemo', 'true');
    setShowDemoDialog(false);
  };

  return (
    <AuthWrapper>
      {/* Add the DemoDialog component right after AuthWrapper */}
      <DemoDialog 
        open={showDemoDialog} 
        onClose={handleCloseDemoDialog}
      />
      
      <Box className="h-screen flex flex-col bg-gray-100 overflow-hidden">
        <IconButton
          onClick={() => setSidebarOpen(true)}
          sx={{
            position: "fixed",
            left: 16,
            top: 16,
            zIndex: 1200,
            backgroundColor: "white",
            boxShadow: 2,
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          <MenuIcon />
        </IconButton>

        <ProjectDrawer
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          projects={projects}
          isLoadingProjects={isLoadingProjects}
          selectedProject={selectedProject}
          handleProjectSelect={handleProjectSelect}
          creditsLoading={creditsLoading}
          credits={credits}
          isLoaded={isLoaded}
          user={user}
          onNewProject={() => setSelectedProject(null)}
        />

        {/* Rest of your app content */}

        {selectedProject && (
          <>
            <NavigationHeader
              currentTab={currentTab}
              handleTabChange={handleTabChange}
              creditsLoading={creditsLoading}
              credits={credits}
              isLoaded={isLoaded}
              user={user}
            />

            <Box
              className="flex-grow overflow-hidden"
              sx={{ ml: { xs: 0, sm: 2 } }}
            >
              <TabPanel value={currentTab} index={0}>
                <ProjectDescriptionTab
                  projectMessages={projectMessages}
                  handleProjectMessage={handleProjectMessage}
                  handleEditMessage={handleEditMessage}
                  handleDeleteMessage={handleDeleteMessage}
                  isLoading={isLoading}
                  requirementSummary={requirementSummary}
                  selectedProject={selectedProject}
                  projectMarkdown={projectMarkdown}
                  handleNextStepFromDescription={handleNextStepFromDescription}
                  gridContainerStyles={gridContainerStyles}
                  leftColumnStyles={leftColumnStyles}
                  rightColumnStyles={rightColumnStyles}
                />
              </TabPanel>

              <TabPanel value={currentTab} index={1}>
                <RequirementsTab
                  requirementMessages={requirementMessages}
                  handleRequirementMessage={handleRequirementMessage}
                  handleEditMessage={handleEditMessage}
                  handleDeleteMessage={handleDeleteMessage}
                  isLoading={isLoading}
                  requirementSummary={requirementSummary}
                  selectedProject={selectedProject}
                  requirementNodes={requirementNodes}
                  setRequirementNodes={setRequirementNodes}
                  requirementEdges={requirementEdges}
                  setRequirementEdges={setRequirementEdges}
                  handleNextStepFromRequirements={
                    handleNextStepFromRequirements
                  }
                  gridContainerStyles={gridContainerStyles}
                  leftColumnStyles={leftColumnStyles}
                  rightColumnStyles={rightColumnStyles}
                  customNodes={customNodes}
                />
              </TabPanel>

              <TabPanel value={currentTab} index={2}>
                <UserStoriesTab
                  userStoryMessages={userStoryMessages}
                  handleUserStoryMessage={handleUserStoryMessage}
                  handleEditMessage={handleEditMessage}
                  handleDeleteMessage={handleDeleteMessage}
                  isLoading={isLoading}
                  userStorySummary={userStorySummary}
                  selectedProject={selectedProject}
                  userStoryNodes={userStoryNodes}
                  setUserStoryNodes={setUserStoryNodes}
                  userStoryEdges={userStoryEdges}
                  setUserStoryEdges={setUserStoryEdges}
                  handleNextStepFromUserStories={handleNextStepFromUserStories}
                  gridContainerStyles={gridContainerStyles}
                  leftColumnStyles={leftColumnStyles}
                  rightColumnStyles={rightColumnStyles}
                  customNodes={customNodes}
                />
              </TabPanel>

              <TabPanel value={currentTab} index={3}>
                <SitemapTab
                  sitemapMessages={sitemapMessages}
                  handleSitemapMessage={handleSitemapMessage}
                  handleEditMessage={handleEditMessage}
                  handleDeleteMessage={handleDeleteMessage}
                  isLoading={isLoading}
                  sitemapSummary={sitemapSummary}
                  selectedProject={selectedProject}
                  sitemapNodes={sitemapNodes}
                  setSitemapNodes={setSitemapNodes}
                  sitemapEdges={sitemapEdges}
                  setSitemapEdges={setSitemapEdges}
                  handleNextStepFromSitemap={handleNextStepFromSitemap}
                  gridContainerStyles={gridContainerStyles}
                  leftColumnStyles={leftColumnStyles}
                  rightColumnStyles={rightColumnStyles}
                  customNodes={customNodes}
                />
              </TabPanel>

              <TabPanel value={currentTab} index={4}>
                <DataModelsTab
                  dataModelMessages={dataModelMessages}
                  handleDataModelMessage={handleDataModelMessage}
                  handleEditMessage={handleEditMessage}
                  handleDeleteMessage={handleDeleteMessage}
                  isLoading={isLoading}
                  dataModelSummary={dataModelSummary}
                  selectedProject={selectedProject}
                  dataModelNodes={dataModelNodes}
                  setDataModelNodes={setDataModelNodes}
                  dataModelEdges={dataModelEdges}
                  setDataModelEdges={setDataModelEdges}
                  handleNextStepFromDataModels={handleNextStepFromDataModels}
                  gridContainerStyles={gridContainerStyles}
                  leftColumnStyles={leftColumnStyles}
                  rightColumnStyles={rightColumnStyles}
                  customNodes={customNodes}
                  deploymentStatus={deploymentStatus}
                  getDeployButtonText={getDeployButtonText}
                  dataSideTab={dataSideTab}
                  handleRightTabChange={handleRightTabChange}
                  generatedSQL={generatedSQL}
                  handleRefreshDataModels={fetchStatus}
                />
              </TabPanel>

              <TabPanel value={currentTab} index={5}>
                <BackendApiTab
                  gridContainerStyles={gridContainerStyles}
                  rightColumnStylesDeployment={rightColumnStylesDeployment}
                  fetchCredits={fetchCredits}
                  selectedProject={selectedProject}
                  iframeKey={iframeKey}
                  setErrorDialogOpen={setErrorDialogOpen}
                  setErrorMessage={setErrorMessage}
                />
              </TabPanel>
            </Box>
          </>
        )}
        {!selectedProject && (
          <NewProjectPage
            onClose={() => setNewProjectDialogOpen(false)}
            onSubmit={handleCreateProject}
            addNewPrompt={handleProjectMessage}
            loading={loading}
          />
        )}
        <EditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSave={handleEditSave}
          initialValue={currentEdit?.text || ""}
          title="Edit Message"
        />

        <ErrorDialog
          open={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          message={errorMessage}
        />

        <div>
          {showPopup &&
            (status === 0 || status === 1 || status === 2 || status === 3) && (
              <Box
                sx={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "#f9f9f9", // Solid light background
                  borderRadius: "12px",
                  color: "#333",
                  padding: "24px",
                  border: "1px solid #ddd",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)", // Slightly stronger shadow
                  zIndex: 1000,
                  textAlign: "center",
                  width: "300px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    marginBottom: "16px",
                    fontWeight: "bold",
                    color: "#333",
                  }}
                >
                  {getPopupMessage(status)}
                </Typography>
                <Button
                  variant="contained"
                  onClick={closePopup}
                  sx={{
                    color: "white",
                    backgroundColor: "#1a1a1a", // Dark close button color
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    boxShadow: "0 4px 6px rgba(26, 26, 26, 0.4)", // Subtle shadow for the button
                    "&:hover": {
                      backgroundColor: "#333", // Slightly lighter on hover
                    },
                  }}
                >
                  Close
                </Button>
              </Box>
            )}
        </div>

        {/* Add this near the end, before the closing AuthWrapper tag */}
        <TutorialModal
          open={tutorialOpen}
          onClose={() => setTutorialOpen(false)}
          videoUrl={currentTutorial.url}
          title={currentTutorial.title}
        />

        {/* Move help Fab to bottom left corner */}
        <Fab
        >
          <HelpOutlineIcon onClick={() => setShowDemo(true)} />
        </Fab>
        {showDemo && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="relative bg-slate-900 rounded-lg w-full max-w-7xl">
              <button
                onClick={() => setShowDemo(false)}
                className="absolute right-4 top-4 text-white hover:text-gray-300 z-10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div style={{
                position: 'relative',
                boxSizing: 'content-box',
                maxHeight: '80vh',
                maxHeight: '80svh',
                width: '100%',
                aspectRatio: '1.7733990147783252',
                padding: '40px 0 40px 0'
              }}>
                <iframe
                  src="https://app.supademo.com/embed/cm6an1etj0l99280i1u6yoibl?embed_v=2"
                  loading="lazy"
                  title="Neoapps Demo"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitallowfullscreen="true"
                  mozallowfullscreen="true"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </Box>
    </AuthWrapper>
  );
}

export default App;
